// @flow
import React, { Fragment, type Element as ReactElement } from 'react';
import {
  ConfigurableAttributes,
  PriceList,
  TierPrice,
} from '../../flowtypes/product';
import SelectInput from '../../components/SelectInput';
import QuantitySelect from '../../components/QuantitySelect';
import TextInput from '../../components/TextInput';
import TextArea from '../../components/TextArea';
import { connect } from 'react-redux';
import PrimaryButton from '../../components/PrimaryButton';
import PromotionalMessaging from '../../components/PromotionalMessaging';
import Show from '../../functional/Show';
import ProductInfo from '../../containers/ProductInfo';
import {
  addToCart,
  CART_STATUS_PROCESSING,
  stockStatusSelector,
  priceSelector,
  CART_STATUS_SUCCESS,
  type MobileAvailability,
  mobileAvailabilitySelector,
  MOBILE_DISPLAY_SHOW_ADD_BUTTON,
  getLowestProductPricing,
  refreshCartStatus as _refreshCartStatus,
  cartStatusUpdate,
} from '../../store/modules/product';
import {
  setAttributeValue as _setAttributeValue,
  setAttributeError as _setAttributeError,
  CART_STATUS_FAILURE,
} from '../../store/modules/attributes';
import { setQuantity as _setQuantity } from '../../store/modules/quantity';
import './ProductForm.css';
import DateSelect from '../../components/DateSelect';
import { match } from '../../helpers/adt';
import { classNames } from '../../helpers/strings';
import {
  Attribute,
  extractAttribute,
  attributeIsCustomOption,
  type $AttributeEntity,
  type $AttributeUnionType,
  filterDependentOptions,
  mapDependentOptionvalues,
} from '../../types/attributes';
import { option } from 'fp-ts';
import RadioInput from '../../components/RadioInput';
import { type Option } from 'fp-ts/lib/Option';
import intersection from 'lodash.intersection';
import {
  setHoveredOption,
  unsetHoveredOption,
} from '../../store/modules/configuration';
import { domready } from '../../helpers/browser';
import { MobileAvailabilityDisplay } from '../../components/MobileAvailability';
import type {
  QuantityOption,
  StateQuantity,
} from '../../store/modules/quantity';
import { optionFind } from '../../helpers/functions';
import { sendAnalyticsForOptionSelection as _sendAnalyticsForOptionSelection } from '../../store/modules/analytics';
import { getBundleOptions } from '../../store/modules/bundles';
import type { BundleOptionView } from '../../flowtypes/bundles';
import { nrPageAction } from '../../helpers/functions'

type Props = {
  attributes: Option<ConfigurableAttributes>,
  addToCartLabel: ?string,
  cartStatus: ?string,
  addToCart: Function,
  setAttributeValue: Function,
  setAttributeError: Function,
  allSelected: Function,
  disabledOptions: Array<number>,
  availableProducts: Array<number>,
  mediaUrl: string,
  baseAttribute: ?string,
  inStock: Option<boolean>,
  isMobileFriendly: boolean,
  setHoveredOption: Function,
  unsetHoveredOption: Function,
  mobileAvailability: MobileAvailability,
  priceList: PriceList,
  setQuantity: Function,
  sendAnalyticsForOptionSelection: Function,
  promotionalMessage: string,
  refreshCartStatus: Function,
  triggerTimeoutFailure: Function,
};

type State = {
  isMounted: boolean
};

const getAttributeAvailableProducts = (availableProducts: Object) => attr =>
  option
    .fromNullable(availableProducts)
    .mapNullable(productsMap => productsMap[attr.id])
    .getOrElseValue([]);

/**
 * getDisabledOptions returns an array of option ids that are considered disabled
 * based on the intersection of that options available products and the products
 * known to be globally available based on the `availableProducts` property in state.configuration.
 *
 * Disabled options are calculated by first filtering out any currently selected options. We then
 * check if there are any products available for each option. If there are none, that option is considered
 * to be disabled.
 *
 * If the given attribute is the only attribute that has a value, there are no disabled options for that attribute.
 */
const getDisabledOptions = (
  availableProductsMap: Object
) => options => attrsWithValue => attributeEntity => {
  if (attributeIsCustomOption(attributeEntity)) {
    return [];
  }

  const attr = extractAttribute(attributeEntity);

  const availableProducts = getAttributeAvailableProducts(availableProductsMap)(
    attr
  );

  if (
    !availableProducts.length ||
    (attrsWithValue.length === 1 && attr.id === attrsWithValue[0].id)
  ) {
    return [];
  }

  const opts = options
    .filter(opt => opt.id !== attr.value)
    .filter(opt => opt.products || false)
    .map(opt => {
      const inter = intersection(availableProducts, opt.products);
      return inter.length <= 0 ? opt.id : null;
    })
    .filter(opt => opt !== null);

  return opts;
};

class ProductAttributeForm extends React.Component<Props, State> {
  state = {
    isMounted: false
  };

  onInputChange: Function;
  renderAttribute: (co: $AttributeUnionType) => ?ReactElement<*>;

  constructor(props) {
    super(props);
    this.onInputChange = this.onInputChange.bind(this);
    this.renderAttribute = this.renderAttribute.bind(this);
    this.renderQuantity = this.renderQuantity.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
  }

  componentDidMount() {
    domready(() => {
      this.setState({
        isMounted: true
      });
    });
  }

  onInputChange(event) {
    const { name, value } = event.target;
    this.props.setAttributeValue(name, value);
    const prettyNameOption = this.props.attributes.chain(xs =>
      optionFind(x => x.id === name, xs)
    );
    const prettyValueOption = prettyNameOption
      .chain(attr =>
        attr.options
          ? optionFind(x => x.id === value, attr.options)
          : option.some({ label: attr.value })
      )
      .map(x => x.label);
    this.props.sendAnalyticsForOptionSelection(
      prettyNameOption.map(x => x.code)
    )(prettyValueOption);
  }

  allSelected(attributesO) {
    return attributesO.map(attributes => attributes.reduce((acc, attribute) => {
      return acc && (attribute.value && attribute.value !== '');
    }, true)).getOrElseValue(false)
  }

  handleAddToCart() {
    this.props.addToCart()
    const timeout = 5000

    setTimeout(() => {
      this.props.refreshCartStatus()
      if (this.props.cartStatus === CART_STATUS_PROCESSING) {
        this.props.triggerTimeoutFailure()
        nrPageAction('PDP_ADDTOCART_TIMEOUT')()
      }
    }, timeout)
  }
  
  renderAttribute(attribute: $AttributeUnionType) {
    const attributeEntity = Attribute(attribute);
    const {
      availableProducts,
      attributes,
      mediaUrl,
      setHoveredOption,
      unsetHoveredOption
    } = this.props;
    const attrs = attributes.getOrElseValue([]);
    const attrsWithValues = attrs.filter(attr => attr.value);
    const defaultAttributeProps = {
      key: attribute.id,
      attribute,
      onMouseEnter: setHoveredOption,
      onMouseLeave: unsetHoveredOption,
      onInputChange: this.onInputChange,
      setAttributeError: this.props.setAttributeError,
      setAttributeValue: this.props.setAttributeValue,
      disabledOptions: getDisabledOptions(availableProducts)(attribute.options)(
        attrsWithValues
      )(attributeEntity),
      required: attribute.required,
      mediaUrl
    };

    const renderAttribute: (_: $AttributeEntity) => ?ReactElement<*> = match({
      Empty: () => null,
      Text: () => <TextInput {...defaultAttributeProps} />,
      Select: () => <SelectInput {...defaultAttributeProps} />,
      Email: () => <TextInput {...defaultAttributeProps} />,
      Textarea: () => <TextArea {...defaultAttributeProps} />,
      Date: () =>
        this.state.isMounted ? <DateSelect {...defaultAttributeProps} /> : null,
      SwatchText: a =>
        a.required && !a.hidden ? (
          <RadioInput {...defaultAttributeProps} />
        ) : null,
      SwatchImage: a =>
        a.required && !a.hidden ? (
          <RadioInput {...defaultAttributeProps} />
        ) : null,
      CustomOption: a =>
        a.required && !a.hidden ? (
          <RadioInput {...defaultAttributeProps} />
        ) : null
    });

    return renderAttribute(attributeEntity);
  }

  renderQuantity(priceList: PriceList, quantity: StateQuantity) {
    const defaultQuantityProps = {
      options: getLowestProductPricing(priceList)
        .tierPrices.sort((a, b) => a.quantity - b.quantity)
        .map((tierPrice: TierPrice, index: number) => {
          const quantityOption: QuantityOption = {
            label: `${tierPrice.quantity} ($${tierPrice.price.toFixed(
              2
            )} per card)`,
            value: tierPrice.quantity
          };
          return quantityOption;
        }),
      onInputChange: event => {
        this.props.setQuantity(event);
      },
      quantity
    };

    const renderQuantity: (_: $AttributeEntity) => ?ReactElement<*> = (
      <QuantitySelect {...defaultQuantityProps} />
    );
    return renderQuantity;
  }

  render() {
    const {
      addToCartLabel,
      cartStatus,
      attributes,
      bundleOptions,
      inStock,
      isMobileFriendly,
      enterEditor,
      mobileAvailabilityDisplay,
      priceList,
      quantity,
      promotionalMessage,
    } = this.props;

    // const isAvailableOnMobile = mobileAvailability
    //   ? mobileAvailability.toLowerCase().includes('yes mobile')
    //   : true;

    const { isMounted } = this.state;
    const isProcessing = cartStatus === CART_STATUS_PROCESSING;
    const disabled = (!isMounted || isProcessing || inStock.isSome()); // || !this.allSelected(attributes);
    const successLabel = enterEditor ? 'Entering Editor...' : 'Added!';
    const buttonLabel =
      cartStatus === CART_STATUS_SUCCESS ? successLabel : addToCartLabel;

    return (
      <div
        id='productForm'
        className={`pdp-form ${attributes
          .map(() => 'pdp-form--configurable')
          .getOrElseValue('')}`}
      >
        {priceList &&
          ((priceList.length > 0 && this.renderQuantity(priceList, quantity)) ||
            (priceList.id &&
              priceList.tierPrices.length > 1 &&
              this.renderQuantity([priceList], quantity)))}

        {attributes
          .map(attrs =>
            attrs
              .filter(filterDependentOptions)
              .map(mapDependentOptionvalues)
              .map(this.renderAttribute)
          )
          .getOrElseValue(null)}

        {bundleOptions
          .map((bundleOptionView: BundleOptionView)=> (
            <Fragment>
              <p className='pdp-form__bundleOption'>
                {bundleOptionView.name} <span className='pdp-form__bundlePrice'>${bundleOptionView.price}</span>
              </p>
            </Fragment>
          ))
        }
        <div
          className={classNames(
            'pdp-form__primaryButtonWrapper',
            mobileAvailabilityDisplay !== MOBILE_DISPLAY_SHOW_ADD_BUTTON
              ? 'pdp-form__primaryButtonWrapper--no-mobile'
              : ''
          )}
        >
          <div className='primaryButtonWrapper__inner'>
            <MobileAvailabilityDisplay
              displayState={mobileAvailabilityDisplay}
            />
            {attributes
              .map(_ => (
                <Fragment>
                  <Show when={!!promotionalMessage}>
                    <PromotionalMessaging>{promotionalMessage}</PromotionalMessaging>
                  </Show>
                </Fragment>
              ))
              .getOrElseValue(null)}
            <div className="primaryButton_productInfo_wrapper" id="pdp-cta">
              <PrimaryButton
                action={this.handleAddToCart}
                disabled={disabled}
                loading={isProcessing}
                text={!disabled ? buttonLabel : "Select Options"}
                isMobileFriendly={isMobileFriendly}
              />
            <div className="new-productInfo-shipping-accordion">
              <ProductInfo />
            </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  attributes: option.fromNullable(state.attributes),
  bundleOptions: getBundleOptions(state.rawProduct),
  addToCartLabel: state.product.addToCartLabel,
  cartStatus: state.product.cartStatus,
  availableProducts: option
    .fromNullable(state.configuration)
    .map(config => (config.availableProducts ? config.availableProducts : []))
    .getOrElseValue([]),

  mediaUrl: state.product.mediaUrl,
  inStock: stockStatusSelector(state),
  isMobileFriendly: state.product.isMobileFriendly,
  price: priceSelector(state),
  productType: state.rawProduct.type_id,
  enterEditor: state.product.enterEditor,
  mobileAvailability: state.product.mobileAvailability,
  mobileAvailabilityDisplay: mobileAvailabilitySelector(state),
  priceList: state.product.priceList,
  quantity: state.quantity,
  promotionalMessage: state.rawProduct.promotional_messaging,
});

const mapDispatchToProps = (dispatch: Function) => ({
  refreshCartStatus: () => dispatch(_refreshCartStatus()),
  triggerTimeoutFailure: () => dispatch(cartStatusUpdate(CART_STATUS_FAILURE)),
  addToCart: () => dispatch(addToCart()),
  setAttributeValue: (key, value) => dispatch(_setAttributeValue(key, value)),
  setAttributeError: (key, error) => dispatch(_setAttributeError(key, error)),
  setHoveredOption: (opt, offsetTop) =>
    dispatch(setHoveredOption(opt, offsetTop)),
  setQuantity: quantity => dispatch(_setQuantity(quantity)),
  unsetHoveredOption: () => dispatch(unsetHoveredOption()),
  sendAnalyticsForOptionSelection: attrLabel => attrValue =>
    _sendAnalyticsForOptionSelection(attrLabel)(attrValue).map(dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProductAttributeForm);



// WEBPACK FOOTER //
// ./src/containers/ProductForm/index.js